import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Wheat, Truck, Package, Scale, Trash2, Plus, RefreshCw,
  Calendar, ChevronLeft, ChevronRight, AlertTriangle, Check,
  Bell, Settings, XCircle, AlertOctagon, Mail, Send, Clock, X,
  ShoppingBag, DollarSign, Recycle, Pencil, History, ClipboardCheck
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const FEED_TYPES = [
  { value: 'layer_mash', label: 'Layer Mash' },
  { value: 'grower_mash', label: 'Grower Mash' },
  { value: 'starter', label: 'Starter' },
  { value: 'broiler_feed', label: 'Broiler Feed' }
];

// Quick-pick reasons for reconciliation adjustments
const REASON_PRESETS = ['Spillage', 'Miscount', 'Transfer', 'Damaged/Spoiled', 'Theft/Loss'];
// Bags variance beyond this is flagged as a high variance for attention
const VARIANCE_THRESHOLD = 5;

const VENDORS = ['SOSE', 'Peak', 'KB4F', 'OTHER'];
const PENS = [1, 2, 3, 4, 5, 6, 7, 8];
const KG_PER_BAG = 25;
const BAGS_PER_DOZEN = 12;

// Helper to get pen label (7 = Brooding Pen, 8 = Grower Pen)
const getPenLabel = (pen) => {
  if (pen === 7) return 'Brooding Pen';
  if (pen === 8) return 'Grower Pen';
  return `Pen ${pen}`;
};

const getFeedTypeLabel = (value) => {
  const found = FEED_TYPES.find(f => f.value === value);
  return found ? found.label : value;
};

const ChickenFeedAudit = () => {
  const [activeTab, setActiveTab] = useState('delivery');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [dailySummary, setDailySummary] = useState(null);
  const [alerts, setAlerts] = useState({ alerts: [], has_alerts: false, alert_count: 0, critical_count: 0 });
  const [alertSettings, setAlertSettings] = useState([]);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState(null);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [notificationLogs, setNotificationLogs] = useState([]);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [newEmailInput, setNewEmailInput] = useState('');
  
  // Empty bags state
  const [emptyBagSummary, setEmptyBagSummary] = useState(null);
  const [emptyBagSaleDialogOpen, setEmptyBagSaleDialogOpen] = useState(false);
  const [emptyBagAdjustDialogOpen, setEmptyBagAdjustDialogOpen] = useState(false);
  const [emptyBagAdjustForm, setEmptyBagAdjustForm] = useState({
    actual_count: '',
    reason: ''
  });
  const [feedAdjustDialogOpen, setFeedAdjustDialogOpen] = useState(false);
  const [feedAdjustForm, setFeedAdjustForm] = useState({
    feed_type: '',
    feed_label: '',
    calculated_closing: 0,
    has_adjustment: false,
    actual_count: '',
    reason: ''
  });
  const [feedLogDialogOpen, setFeedLogDialogOpen] = useState(false);
  const [feedLogs, setFeedLogs] = useState([]);
  const [feedLogLoading, setFeedLogLoading] = useState(false);
  const [feedLogFilter, setFeedLogFilter] = useState('all');
  const [stockTakeDialogOpen, setStockTakeDialogOpen] = useState(false);
  const [stockTakeForm, setStockTakeForm] = useState({ reason: '', counts: {} });
  const [emptyBagSaleForm, setEmptyBagSaleForm] = useState({
    date: '',
    dozens_sold: '',
    price_per_dozen: '',
    buyer_name: '',
    notes: ''
  });
  
  // Dialog states
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
  const [usageDialogOpen, setUsageDialogOpen] = useState(false);
  
  // Edit states
  const [editingDelivery, setEditingDelivery] = useState(null);
  const [editingUsage, setEditingUsage] = useState(null);
  const [editingEmptyBagSale, setEditingEmptyBagSale] = useState(null);
  
  // Form states
  const [deliveryForm, setDeliveryForm] = useState({
    delivery_date: '',
    feed_type: '',
    vendor: '',
    bags_delivered: '',
    damaged_bags: '0',
    returned_to_vendor: false,
    notes: ''
  });
  
  const [usageForm, setUsageForm] = useState({
    usage_date: '',
    pen_number: '',
    feed_type: '',
    bags_taken: '',
    bags_consumed: '',
    notes: ''
  });
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  const isManager = user.role === 'manager';

  useEffect(() => {
    fetchDailySummary();
    fetchAlerts();
    fetchEmptyBagSummary();
  }, [selectedDate]);

  const fetchDailySummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/chicken-feed/daily-summary/${selectedDate}`, getAuthHeader());
      setDailySummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
      toast.error('Failed to load feed audit data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/chicken-feed/alerts?date=${selectedDate}`, getAuthHeader());
      setAlerts(response.data);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  const fetchAlertSettings = async () => {
    try {
      const response = await axios.get(`${API}/chicken-feed/alert-settings`, getAuthHeader());
      setAlertSettings(response.data);
    } catch (error) {
      console.error('Error fetching alert settings:', error);
      toast.error('Failed to load alert settings');
    }
  };

  const updateAlertSetting = async (feedType, field, value) => {
    try {
      await axios.put(`${API}/chicken-feed/alert-settings/${feedType}`, {
        [field]: value
      }, getAuthHeader());
      toast.success('Alert setting updated');
      fetchAlertSettings();
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to update setting');
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      const response = await axios.get(`${API}/chicken-feed/notification-settings`, getAuthHeader());
      setNotificationSettings(response.data);
    } catch (error) {
      console.error('Error fetching notification settings:', error);
    }
  };

  const fetchNotificationLogs = async () => {
    try {
      const response = await axios.get(`${API}/chicken-feed/notification-logs?limit=10`, getAuthHeader());
      setNotificationLogs(response.data);
    } catch (error) {
      console.error('Error fetching notification logs:', error);
    }
  };

  const updateNotificationSetting = async (field, value) => {
    try {
      await axios.put(`${API}/chicken-feed/notification-settings`, {
        [field]: value
      }, getAuthHeader());
      toast.success('Notification setting updated');
      fetchNotificationSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update setting');
    }
  };

  const addNotificationEmail = async () => {
    if (!newEmailInput || !newEmailInput.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    const emails = [...(notificationSettings?.notification_emails || []), newEmailInput];
    await updateNotificationSetting('notification_emails', emails);
    setNewEmailInput('');
  };

  const removeNotificationEmail = async (emailToRemove) => {
    const emails = (notificationSettings?.notification_emails || []).filter(e => e !== emailToRemove);
    await updateNotificationSetting('notification_emails', emails);
  };

  const sendManualNotification = async (force = false) => {
    setSendingNotification(true);
    try {
      const response = await axios.post(
        `${API}/chicken-feed/send-alert-notification?date=${selectedDate}&force=${force}`, 
        {}, 
        getAuthHeader()
      );
      toast.success(response.data.message);
      fetchNotificationLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send notification');
    } finally {
      setSendingNotification(false);
    }
  };

  // Empty bag functions
  const fetchEmptyBagSummary = async () => {
    try {
      const response = await axios.get(`${API}/empty-feed-bags/summary/${selectedDate}`, getAuthHeader());
      setEmptyBagSummary(response.data);
    } catch (error) {
      console.error('Error fetching empty bag summary:', error);
    }
  };

  const handleEmptyBagSaleSubmit = async (e) => {
    e.preventDefault();
    if (!emptyBagSaleForm.dozens_sold || parseInt(emptyBagSaleForm.dozens_sold) < 1) {
      toast.error('Please enter at least 1 dozen');
      return;
    }
    
    const saleData = {
      date: emptyBagSaleForm.date || selectedDate,
      dozens_sold: parseInt(emptyBagSaleForm.dozens_sold),
      price_per_dozen: parseFloat(emptyBagSaleForm.price_per_dozen) || 0,
      buyer_name: emptyBagSaleForm.buyer_name || null,
      notes: emptyBagSaleForm.notes || null
    };
    
    try {
      if (editingEmptyBagSale) {
        // Update existing sale
        await axios.put(`${API}/empty-feed-bags/sale/${editingEmptyBagSale.id}`, saleData, getAuthHeader());
        toast.success('Sale updated successfully');
      } else {
        // Create new sale
        await axios.post(`${API}/empty-feed-bags/sale`, saleData, getAuthHeader());
        toast.success('Empty bag sale recorded');
      }
      
      setEmptyBagSaleDialogOpen(false);
      setEditingEmptyBagSale(null);
      setEmptyBagSaleForm({
        date: '',
        dozens_sold: '',
        price_per_dozen: '',
        buyer_name: '',
        notes: ''
      });
      fetchEmptyBagSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save sale');
    }
  };

  const handleEditEmptyBagSale = (sale) => {
    setEditingEmptyBagSale(sale);
    setEmptyBagSaleForm({
      date: sale.date,
      dozens_sold: String(sale.dozens_sold),
      price_per_dozen: String(sale.price_per_dozen || 0),
      buyer_name: sale.buyer_name || '',
      notes: sale.notes || ''
    });
    setEmptyBagSaleDialogOpen(true);
  };

  const handleDeleteEmptyBagSale = async (saleId) => {
    if (!window.confirm('Are you sure you want to delete this sale record?')) return;
    
    try {
      await axios.delete(`${API}/empty-feed-bags/sale/${saleId}`, getAuthHeader());
      toast.success('Sale deleted');
      fetchEmptyBagSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete sale');
    }
  };

  const handleEmptyBagAdjustSubmit = async (e) => {
    e.preventDefault();
    if (!emptyBagAdjustForm.actual_count || !emptyBagAdjustForm.reason) {
      toast.error('Please enter the actual count and reason');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/empty-feed-bags/adjustment`, {
        date: selectedDate,
        actual_count: parseInt(emptyBagAdjustForm.actual_count),
        reason: emptyBagAdjustForm.reason
      }, getAuthHeader());
      
      toast.success(response.data.message);
      setEmptyBagAdjustDialogOpen(false);
      setEmptyBagAdjustForm({ actual_count: '', reason: '' });
      fetchEmptyBagSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust inventory');
    }
  };

  const handleDeleteEmptyBagAdjustment = async () => {
    if (!window.confirm('Are you sure you want to remove the manual adjustment for this date?')) return;
    
    try {
      await axios.delete(`${API}/empty-feed-bags/adjustment/${selectedDate}`, getAuthHeader());
      toast.success('Adjustment removed');
      fetchEmptyBagSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove adjustment');
    }
  };

  const openFeedAdjustDialog = (feedType, label, data) => {
    const inv = data?.inventory || {};
    const calculatedClosing = (inv.opening || 0) + (inv.delivered || 0) - (inv.used || 0) + (inv.returned || 0) - (inv.damaged || 0);
    setFeedAdjustForm({
      feed_type: feedType,
      feed_label: label,
      calculated_closing: calculatedClosing,
      has_adjustment: (inv.manual_adjustment || 0) !== 0,
      actual_count: String(inv.closing ?? calculatedClosing),
      reason: ''
    });
    setFeedAdjustDialogOpen(true);
  };

  const handleFeedAdjustSubmit = async (e) => {
    e.preventDefault();
    if (feedAdjustForm.actual_count === '' || !feedAdjustForm.reason) {
      toast.error('Please enter the actual count and reason');
      return;
    }
    try {
      const response = await axios.post(`${API}/chicken-feed/adjustment`, {
        date: selectedDate,
        feed_type: feedAdjustForm.feed_type,
        actual_count: parseInt(feedAdjustForm.actual_count),
        reason: feedAdjustForm.reason
      }, getAuthHeader());
      toast.success(response.data.message);
      setFeedAdjustDialogOpen(false);
      fetchDailySummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust closing balance');
    }
  };

  const handleDeleteFeedAdjustment = async (feedType) => {
    if (!window.confirm('Remove the manual closing adjustment for this feed type on this date?')) return;
    try {
      await axios.delete(`${API}/chicken-feed/adjustment/${selectedDate}/${feedType}`, getAuthHeader());
      toast.success('Adjustment removed');
      fetchDailySummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove adjustment');
    }
  };

  const openFeedLogDialog = async (feedType = 'all') => {
    setFeedLogFilter(feedType);
    setFeedLogDialogOpen(true);
    setFeedLogLoading(true);
    try {
      const url = feedType && feedType !== 'all'
        ? `${API}/chicken-feed/adjustment-log?feed_type=${feedType}`
        : `${API}/chicken-feed/adjustment-log`;
      const response = await axios.get(url, getAuthHeader());
      setFeedLogs(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load adjustment log');
    } finally {
      setFeedLogLoading(false);
    }
  };

  const openStockTakeDialog = () => {
    const counts = {};
    FEED_TYPES.forEach(ft => {
      counts[ft.value] = String(dailySummary?.summary?.[ft.value]?.inventory?.closing ?? 0);
    });
    setStockTakeForm({ reason: '', counts });
    setStockTakeDialogOpen(true);
  };

  const handleStockTakeSubmit = async (e) => {
    e.preventDefault();
    if (!stockTakeForm.reason) {
      toast.error('Please enter a reason for the stock-take');
      return;
    }
    const items = FEED_TYPES
      .filter(ft => stockTakeForm.counts[ft.value] !== '' && stockTakeForm.counts[ft.value] !== undefined)
      .map(ft => ({ feed_type: ft.value, actual_count: parseInt(stockTakeForm.counts[ft.value]) }));
    if (items.length === 0) {
      toast.error('Enter at least one actual count');
      return;
    }
    try {
      const response = await axios.post(`${API}/chicken-feed/adjustment/bulk`, {
        date: selectedDate,
        reason: stockTakeForm.reason,
        items
      }, getAuthHeader());
      toast.success(response.data.message);
      setStockTakeDialogOpen(false);
      fetchDailySummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply stock-take');
    }
  };

  const handleDateChange = (days) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + days);
    setSelectedDate(current.toISOString().split('T')[0]);
  };

  const handleDeliverySubmit = async (e) => {
    e.preventDefault();
    if (!deliveryForm.feed_type || !deliveryForm.vendor || !deliveryForm.bags_delivered) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    const deliveryData = {
      date: deliveryForm.delivery_date || selectedDate,
      feed_type: deliveryForm.feed_type,
      vendor: deliveryForm.vendor,
      bags_delivered: parseInt(deliveryForm.bags_delivered),
      damaged_bags: parseInt(deliveryForm.damaged_bags) || 0,
      returned_to_vendor: deliveryForm.returned_to_vendor,
      notes: deliveryForm.notes || null
    };
    
    try {
      if (editingDelivery) {
        // Update existing delivery
        await axios.put(`${API}/chicken-feed/delivery/${editingDelivery.id}`, deliveryData, getAuthHeader());
        toast.success('Delivery updated successfully');
      } else {
        // Create new delivery
        await axios.post(`${API}/chicken-feed/delivery`, deliveryData, getAuthHeader());
        toast.success('Delivery recorded successfully');
      }
      
      setDeliveryDialogOpen(false);
      setEditingDelivery(null);
      setDeliveryForm({
        delivery_date: '',
        feed_type: '',
        vendor: '',
        bags_delivered: '',
        damaged_bags: '0',
        returned_to_vendor: false,
        notes: ''
      });
      fetchDailySummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save delivery');
    }
  };

  const handleEditDelivery = (delivery) => {
    setEditingDelivery(delivery);
    setDeliveryForm({
      delivery_date: delivery.date,
      feed_type: delivery.feed_type,
      vendor: delivery.vendor,
      bags_delivered: String(delivery.bags_delivered),
      damaged_bags: String(delivery.damaged_bags || 0),
      returned_to_vendor: delivery.returned_to_vendor || false,
      notes: delivery.notes || ''
    });
    setDeliveryDialogOpen(true);
  };

  const handleUsageSubmit = async (e) => {
    e.preventDefault();
    if (!usageForm.pen_number || !usageForm.feed_type || !usageForm.bags_taken || !usageForm.bags_consumed) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    const taken = parseInt(usageForm.bags_taken);
    const consumed = parseInt(usageForm.bags_consumed);
    
    if (consumed > taken) {
      toast.error('Bags consumed cannot exceed bags taken');
      return;
    }
    
    const usageData = {
      date: usageForm.usage_date || selectedDate,
      pen_number: parseInt(usageForm.pen_number),
      feed_type: usageForm.feed_type,
      bags_taken: taken,
      bags_consumed: consumed,
      notes: usageForm.notes || null
    };
    
    try {
      if (editingUsage) {
        // Update existing usage
        await axios.put(`${API}/chicken-feed/pen-usage/${editingUsage.id}`, usageData, getAuthHeader());
        toast.success('Usage record updated successfully');
      } else {
        // Create new usage
        await axios.post(`${API}/chicken-feed/pen-usage`, usageData, getAuthHeader());
        toast.success('Pen usage recorded successfully');
      }
      
      setUsageDialogOpen(false);
      setEditingUsage(null);
      setUsageForm({
        usage_date: '',
        pen_number: '',
        feed_type: '',
        bags_taken: '',
        bags_consumed: '',
        notes: ''
      });
      fetchDailySummary();
      fetchEmptyBagSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save usage');
    }
  };

  const handleEditUsage = (usage) => {
    setEditingUsage(usage);
    setUsageForm({
      usage_date: usage.date,
      pen_number: String(usage.pen_number),
      feed_type: usage.feed_type,
      bags_taken: String(usage.bags_taken),
      bags_consumed: String(usage.bags_consumed),
      notes: usage.notes || ''
    });
    setUsageDialogOpen(true);
  };

  const handleDeleteDelivery = async (deliveryId) => {
    if (!window.confirm('Are you sure you want to delete this delivery record?')) return;
    
    try {
      await axios.delete(`${API}/chicken-feed/delivery/${deliveryId}`, getAuthHeader());
      toast.success('Delivery deleted');
      fetchDailySummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete delivery');
    }
  };

  const handleDeleteUsage = async (usageId) => {
    if (!window.confirm('Are you sure you want to delete this usage record?')) return;
    
    try {
      await axios.delete(`${API}/chicken-feed/pen-usage/${usageId}`, getAuthHeader());
      toast.success('Usage record deleted');
      fetchDailySummary();
      fetchEmptyBagSummary(); // Update empty bags when usage is deleted
      fetchDailySummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete usage record');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-NG', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Calculate totals
  const calculateTotals = () => {
    if (!dailySummary?.summary) return { totalBags: 0, totalKg: 0 };
    
    let totalBags = 0;
    let totalKg = 0;
    
    Object.values(dailySummary.summary).forEach(feed => {
      totalKg += feed.total_kg_consumed || 0;
      Object.values(feed.pen_consumption || {}).forEach(pen => {
        totalBags += pen.bags_consumed || 0;
      });
    });
    
    return { totalBags, totalKg };
  };

  const totals = calculateTotals();

  return (
    <div className="space-y-6" data-testid="chicken-feed-audit-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Chicken Feed Audit
          </h1>
          <p className="text-slate-600 mt-1">Track feed deliveries, pen consumption, and daily inventory</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => handleDateChange(-1)}
            data-testid="prev-day-btn"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2 px-3 py-2 bg-white border rounded-lg">
            <Calendar className="h-4 w-4 text-slate-500" />
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="border-0 p-0 h-auto w-auto focus-visible:ring-0"
              data-testid="date-picker"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => handleDateChange(1)}
            data-testid="next-day-btn"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Date Display */}
      <div className="text-center py-2 bg-emerald-50 rounded-lg border border-emerald-200">
        <p className="text-emerald-800 font-medium">{formatDate(selectedDate)}</p>
      </div>

      {/* Alerts Banner */}
      {alerts.has_alerts && (
        <div className={`rounded-xl border-2 p-4 ${
          alerts.critical_count > 0 
            ? 'bg-red-50 border-red-300' 
            : 'bg-amber-50 border-amber-300'
        }`} data-testid="alerts-banner">
          <div className="flex items-start gap-3">
            {alerts.critical_count > 0 ? (
              <AlertOctagon className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <h3 className={`font-semibold ${alerts.critical_count > 0 ? 'text-red-800' : 'text-amber-800'}`}>
                {alerts.critical_count > 0 ? 'Critical Stock Alert!' : 'Low Stock Warning'}
              </h3>
              <div className="mt-2 space-y-1">
                {alerts.alerts.map((alert, idx) => (
                  <div key={idx} className={`flex items-center gap-2 text-sm ${
                    alert.alert_level === 'out_of_stock' ? 'text-red-700 font-bold' :
                    alert.alert_level === 'critical' ? 'text-red-600 font-semibold' :
                    'text-amber-700'
                  }`}>
                    {alert.alert_level === 'out_of_stock' && <XCircle className="w-4 h-4" />}
                    {alert.alert_level === 'critical' && <AlertOctagon className="w-4 h-4" />}
                    {alert.alert_level === 'low' && <AlertTriangle className="w-4 h-4" />}
                    <span>{alert.message}</span>
                  </div>
                ))}
              </div>
            </div>
            {isAdmin && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  fetchAlertSettings();
                  setSettingsDialogOpen(true);
                }}
                className="flex-shrink-0"
              >
                <Settings className="w-4 h-4 mr-1" />
                Settings
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('delivery')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'delivery' 
              ? 'text-emerald-600 border-emerald-600' 
              : 'text-slate-500 border-transparent hover:text-slate-700'
          }`}
          data-testid="delivery-tab"
        >
          <Truck className="w-4 h-4 inline mr-2" />
          Deliveries
        </button>
        <button
          onClick={() => setActiveTab('usage')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'usage' 
              ? 'text-emerald-600 border-emerald-600' 
              : 'text-slate-500 border-transparent hover:text-slate-700'
          }`}
          data-testid="usage-tab"
        >
          <Package className="w-4 h-4 inline mr-2" />
          Pen Usage
        </button>
        <button
          onClick={() => setActiveTab('summary')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'summary' 
              ? 'text-emerald-600 border-emerald-600' 
              : 'text-slate-500 border-transparent hover:text-slate-700'
          }`}
          data-testid="summary-tab"
        >
          <Scale className="w-4 h-4 inline mr-2" />
          Daily Summary
        </button>
        <button
          onClick={() => {
            setActiveTab('alerts');
            fetchAlertSettings();
            fetchNotificationSettings();
          }}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'alerts' 
              ? 'text-emerald-600 border-emerald-600' 
              : 'text-slate-500 border-transparent hover:text-slate-700'
          }`}
          data-testid="alerts-tab"
        >
          <Bell className="w-4 h-4 inline mr-2" />
          Alerts
          {alerts.alert_count > 0 && (
            <span className={`ml-2 px-1.5 py-0.5 text-xs rounded-full ${
              alerts.critical_count > 0 ? 'bg-red-500 text-white' : 'bg-amber-500 text-white'
            }`}>
              {alerts.alert_count}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('empty-bags')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'empty-bags' 
              ? 'text-emerald-600 border-emerald-600' 
              : 'text-slate-500 border-transparent hover:text-slate-700'
          }`}
          data-testid="empty-bags-tab"
        >
          <Recycle className="w-4 h-4 inline mr-2" />
          Empty Bags
          {emptyBagSummary?.inventory?.closing > 0 && (
            <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-slate-500 text-white">
              {emptyBagSummary.inventory.closing}
            </span>
          )}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
          <span className="ml-3 text-slate-600">Loading...</span>
        </div>
      ) : (
        <>
          {/* Delivery Tab */}
          {activeTab === 'delivery' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-slate-800">Feed Deliveries</h2>
                <Button 
                  onClick={() => setDeliveryDialogOpen(true)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="add-delivery-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Record Delivery
                </Button>
              </div>

              {/* Delivery List by Feed Type */}
              {FEED_TYPES.map(feedType => {
                const deliveries = dailySummary?.summary?.[feedType.value]?.deliveries || [];
                const inventory = dailySummary?.summary?.[feedType.value]?.inventory || {};
                
                return (
                  <div key={feedType.value} className="bg-white rounded-xl border p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Wheat className="w-5 h-5 text-amber-600" />
                        <h3 className="font-semibold text-slate-800">{feedType.label}</h3>
                      </div>
                      <div className="text-sm text-slate-600">
                        Delivered Today: <span className="font-bold text-emerald-600">{inventory.delivered || 0} bags</span>
                      </div>
                    </div>
                    
                    {deliveries.length === 0 ? (
                      <p className="text-slate-500 text-sm italic">No deliveries recorded for this feed type today</p>
                    ) : (
                      <div className="space-y-2">
                        {deliveries.map((delivery) => (
                          <div 
                            key={delivery.id} 
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                            data-testid={`delivery-${delivery.id}`}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                  {delivery.vendor}
                                </span>
                                <span className="font-medium">{delivery.bags_delivered} bags delivered</span>
                                {delivery.damaged_bags > 0 && (
                                  <span className="flex items-center gap-1 text-red-600 text-sm">
                                    <AlertTriangle className="w-3 h-3" />
                                    {delivery.damaged_bags} damaged
                                    {delivery.returned_to_vendor && (
                                      <span className="text-green-600 ml-1">(returned)</span>
                                    )}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-slate-500 mt-1">
                                Net: {delivery.net_bags} bags • Recorded by: {delivery.recorded_by_name}
                                {delivery.notes && <span className="ml-2">• {delivery.notes}</span>}
                              </div>
                            </div>
                            {isAdmin && (
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditDelivery(delivery)}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  data-testid={`edit-delivery-${delivery.id}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteDelivery(delivery.id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-delivery-${delivery.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Usage Tab */}
          {activeTab === 'usage' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-slate-800">Pen Feed Usage</h2>
                <Button 
                  onClick={() => setUsageDialogOpen(true)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="add-usage-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Record Usage
                </Button>
              </div>

              {/* Usage by Pen */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {PENS.map(pen => {
                  // Collect all usage for this pen across feed types
                  const penUsage = [];
                  FEED_TYPES.forEach(feedType => {
                    const consumption = dailySummary?.summary?.[feedType.value]?.pen_consumption?.[pen];
                    if (consumption && consumption.bags_taken > 0) {
                      penUsage.push({
                        feed_type: feedType.value,
                        label: feedType.label,
                        ...consumption
                      });
                    }
                  });

                  // Get individual records for deletion
                  const usageRecords = [];
                  FEED_TYPES.forEach(feedType => {
                    const deliveries = dailySummary?.summary?.[feedType.value]?.deliveries || [];
                    // Note: we need usage records, not deliveries - they come from pen_usage endpoint
                  });

                  const totalKg = penUsage.reduce((sum, u) => sum + (u.kg_consumed || 0), 0);

                  return (
                    <div key={pen} className="bg-white rounded-xl border p-4" data-testid={`pen-${pen}-card`}>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-slate-800">{getPenLabel(pen)}</h3>
                        {totalKg > 0 && (
                          <span className="text-sm font-medium text-purple-600">
                            {totalKg} kg consumed
                          </span>
                        )}
                      </div>
                      
                      {penUsage.length === 0 ? (
                        <p className="text-slate-500 text-sm italic">No feed usage recorded</p>
                      ) : (
                        <div className="space-y-2">
                          {penUsage.map((usage, idx) => (
                            <div key={idx} className="p-2 bg-slate-50 rounded text-sm">
                              <div className="flex justify-between items-center">
                                <span className="font-medium">{usage.label}</span>
                                <span className="text-emerald-600">{usage.kg_consumed} kg</span>
                              </div>
                              <div className="text-xs text-slate-500 mt-1">
                                Taken: {usage.bags_taken} • Consumed: {usage.bags_consumed} • Returned: {usage.bags_returned}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Usage Records List */}
              <div className="bg-white rounded-xl border p-4 mt-6">
                <h3 className="font-semibold text-slate-800 mb-4">All Usage Records</h3>
                <UsageRecordsList 
                  dailySummary={dailySummary} 
                  selectedDate={selectedDate}
                  onDelete={handleDeleteUsage}
                  onEdit={handleEditUsage}
                  canDelete={isAdmin}
                  canEdit={isAdmin}
                />
              </div>
            </div>
          )}

          {/* Summary Tab */}
          {activeTab === 'summary' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-slate-800">Daily Inventory Summary</h2>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => openFeedLogDialog('all')}
                    data-testid="view-adjustment-log-btn"
                  >
                    <History className="w-4 h-4 mr-1.5" />
                    Reconciliation Log
                  </Button>
                  {isAdmin && (
                    <Button
                      type="button"
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={openStockTakeDialog}
                      data-testid="stock-take-btn"
                    >
                      <ClipboardCheck className="w-4 h-4 mr-1.5" />
                      Stock Take
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Overall Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white">
                  <p className="text-emerald-100 text-sm">Total Consumed</p>
                  <p className="text-2xl font-bold">{totals.totalBags} bags</p>
                  <p className="text-emerald-100 text-sm">{totals.totalKg} kg</p>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
                  <p className="text-blue-100 text-sm">Bag Weight</p>
                  <p className="text-2xl font-bold">{KG_PER_BAG} kg</p>
                  <p className="text-blue-100 text-sm">per bag</p>
                </div>
                <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white">
                  <p className="text-amber-100 text-sm">Feed Types</p>
                  <p className="text-2xl font-bold">{FEED_TYPES.length}</p>
                  <p className="text-amber-100 text-sm">tracked</p>
                </div>
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
                  <p className="text-purple-100 text-sm">Pens</p>
                  <p className="text-2xl font-bold">{PENS.length}</p>
                  <p className="text-purple-100 text-sm">monitored</p>
                </div>
              </div>

              {/* Feed Type Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {FEED_TYPES.map(feedType => {
                  const data = dailySummary?.summary?.[feedType.value];
                  const inv = data?.inventory || {};
                  const penConsumption = data?.pen_consumption || {};
                  
                  return (
                    <div key={feedType.value} className="bg-white rounded-xl border p-4" data-testid={`summary-${feedType.value}`}>
                      <div className="flex items-center gap-2 mb-4">
                        <Wheat className="w-5 h-5 text-amber-600" />
                        <h3 className="font-semibold text-slate-800">{feedType.label}</h3>
                      </div>
                      
                      {/* Inventory Flow */}
                      <div className="grid grid-cols-3 gap-2 text-center mb-4">
                        <div className="p-2 bg-slate-50 rounded">
                          <p className="text-xs text-slate-500">Opening</p>
                          <p className="text-lg font-bold text-slate-700">{inv.opening || 0}</p>
                        </div>
                        <div className="p-2 bg-green-50 rounded">
                          <p className="text-xs text-green-600">+ Delivered</p>
                          <p className="text-lg font-bold text-green-700">{inv.delivered || 0}</p>
                        </div>
                        <div className="p-2 bg-red-50 rounded">
                          <p className="text-xs text-red-600">- Used</p>
                          <p className="text-lg font-bold text-red-700">{inv.used || 0}</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2 text-center mb-4">
                        <div className="p-2 bg-blue-50 rounded">
                          <p className="text-xs text-blue-600">+ Returned</p>
                          <p className="text-lg font-bold text-blue-700">{inv.returned || 0}</p>
                        </div>
                        <div className="p-2 bg-amber-50 rounded">
                          <p className="text-xs text-amber-600">- Damaged</p>
                          <p className="text-lg font-bold text-amber-700">{inv.damaged || 0}</p>
                        </div>
                        <div className={`p-2 rounded border-2 relative ${Math.abs(inv.manual_adjustment || 0) >= VARIANCE_THRESHOLD ? 'bg-red-50 border-red-300' : 'bg-emerald-100 border-emerald-300'}`}>
                          <p className={`text-xs ${Math.abs(inv.manual_adjustment || 0) >= VARIANCE_THRESHOLD ? 'text-red-600' : 'text-emerald-600'}`}>= Closing</p>
                          <p className={`text-lg font-bold ${Math.abs(inv.manual_adjustment || 0) >= VARIANCE_THRESHOLD ? 'text-red-700' : 'text-emerald-700'}`}>{inv.closing || 0}</p>
                          {isAdmin && (
                            <button
                              type="button"
                              onClick={() => openFeedAdjustDialog(feedType.value, feedType.label, data)}
                              className="absolute top-1 right-1 p-1 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-200 rounded"
                              title="Edit closing balance (reconcile with actual count)"
                              data-testid={`edit-closing-${feedType.value}`}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Manual adjustment note */}
                      {(inv.manual_adjustment || 0) !== 0 && (
                        <div className={`flex items-center justify-between border rounded-lg px-3 py-2 mb-3 ${Math.abs(inv.manual_adjustment || 0) >= VARIANCE_THRESHOLD ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`} data-testid={`adjustment-note-${feedType.value}`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`text-xs ${Math.abs(inv.manual_adjustment || 0) >= VARIANCE_THRESHOLD ? 'text-red-700' : 'text-amber-700'}`}>
                              Manually adjusted: <strong>{inv.manual_adjustment > 0 ? '+' : ''}{inv.manual_adjustment}</strong> bags to match actual count
                            </p>
                            {Math.abs(inv.manual_adjustment || 0) >= VARIANCE_THRESHOLD && (
                              <Badge className="bg-red-600 hover:bg-red-600 text-white text-[10px] px-1.5 py-0" data-testid={`variance-badge-${feedType.value}`}>
                                <AlertTriangle className="w-3 h-3 mr-1" /> High variance
                              </Badge>
                            )}
                          </div>
                          {isAdmin && (
                            <button
                              type="button"
                              onClick={() => handleDeleteFeedAdjustment(feedType.value)}
                              className="text-xs text-red-600 hover:text-red-800 font-medium ml-2"
                              data-testid={`remove-adjustment-${feedType.value}`}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      )}

                      {/* Pen Breakdown */}
                      {Object.keys(penConsumption).length > 0 && (
                        <div className="border-t pt-3 mt-3">
                          <p className="text-xs text-slate-500 mb-2">Consumption by Pen:</p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(penConsumption).map(([pen, data]) => (
                              <span key={pen} className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs">
                                {getPenLabel(parseInt(pen))}: {data.kg_consumed}kg
                              </span>
                            ))}
                          </div>
                          <p className="text-sm font-medium text-purple-600 mt-2">
                            Total: {data?.total_kg_consumed || 0} kg consumed
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Carryover Note */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-800">Daily Carryover</p>
                    <p className="text-sm text-blue-700">
                      Each feed type's closing balance automatically becomes the next day's opening balance.
                      Unused bags returned from pens are added back to inventory.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Alerts Tab */}
          {activeTab === 'alerts' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-slate-800">Feed Stock Alerts</h2>
                {isAdmin && (
                  <Button 
                    variant="outline"
                    onClick={() => {
                      fetchAlertSettings();
                      setSettingsDialogOpen(true);
                    }}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Configure Thresholds
                  </Button>
                )}
              </div>

              {/* Current Alerts */}
              {alerts.has_alerts ? (
                <div className="space-y-3">
                  {alerts.alerts.map((alert, idx) => (
                    <div 
                      key={idx}
                      className={`rounded-xl border-2 p-4 ${
                        alert.alert_level === 'out_of_stock' ? 'bg-red-100 border-red-400' :
                        alert.alert_level === 'critical' ? 'bg-red-50 border-red-300' :
                        'bg-amber-50 border-amber-300'
                      }`}
                      data-testid={`alert-${alert.feed_type}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {alert.alert_level === 'out_of_stock' && <XCircle className="w-6 h-6 text-red-600" />}
                          {alert.alert_level === 'critical' && <AlertOctagon className="w-6 h-6 text-red-500" />}
                          {alert.alert_level === 'low' && <AlertTriangle className="w-6 h-6 text-amber-500" />}
                          <div>
                            <h3 className={`font-semibold ${
                              alert.alert_level === 'out_of_stock' ? 'text-red-800' :
                              alert.alert_level === 'critical' ? 'text-red-700' :
                              'text-amber-800'
                            }`}>
                              {alert.feed_label}
                            </h3>
                            <p className="text-sm text-slate-600">
                              Current stock: <span className="font-bold">{alert.current_stock} bags</span>
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            alert.alert_level === 'out_of_stock' ? 'bg-red-200 text-red-800' :
                            alert.alert_level === 'critical' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {alert.alert_level === 'out_of_stock' ? 'OUT OF STOCK' :
                             alert.alert_level === 'critical' ? 'CRITICAL' : 'LOW STOCK'}
                          </span>
                          <p className="text-xs text-slate-500 mt-1">
                            Threshold: {alert.low_threshold} bags
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
                  <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-green-800">All Stock Levels Normal</h3>
                  <p className="text-green-600 mt-1">
                    No feed types are below their alert thresholds for {formatDate(selectedDate)}.
                  </p>
                </div>
              )}

              {/* Alert Thresholds Overview */}
              <div className="bg-white rounded-xl border p-4 mt-6">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-slate-500" />
                  Alert Thresholds
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {FEED_TYPES.map(feedType => {
                    const settings = alertSettings.find(s => s.feed_type === feedType.value) || {
                      low_stock_threshold: 10,
                      critical_stock_threshold: 5,
                      is_enabled: true
                    };
                    const inv = dailySummary?.summary?.[feedType.value]?.inventory || {};
                    const closing = inv.closing || 0;
                    
                    return (
                      <div key={feedType.value} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Wheat className="w-4 h-4 text-amber-600" />
                          <span className="font-medium text-slate-700">{feedType.label}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-slate-500">
                            Current: <span className={`font-bold ${
                              closing <= 0 ? 'text-red-600' :
                              closing < settings.critical_stock_threshold ? 'text-red-500' :
                              closing < settings.low_stock_threshold ? 'text-amber-500' :
                              'text-green-600'
                            }`}>{closing}</span>
                          </span>
                          <span className="text-slate-400">|</span>
                          <span className="text-amber-600">Low: {settings.low_stock_threshold}</span>
                          <span className="text-slate-400">|</span>
                          <span className="text-red-600">Crit: {settings.critical_stock_threshold}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Email Notifications Section - Admin Only */}
              {isAdmin && (
                <div className="bg-white rounded-xl border p-4 mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                      <Mail className="w-5 h-5 text-blue-500" />
                      Email Notifications
                    </h3>
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        fetchNotificationSettings();
                        fetchNotificationLogs();
                        setNotificationDialogOpen(true);
                      }}
                    >
                      <Settings className="w-4 h-4 mr-1" />
                      Configure
                    </Button>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-700">
                        {notificationSettings?.is_enabled ? (
                          <span className="text-green-600">Notifications Enabled</span>
                        ) : (
                          <span className="text-slate-500">Notifications Disabled</span>
                        )}
                      </p>
                      <p className="text-sm text-slate-500">
                        {notificationSettings?.notification_emails?.length || 0} recipient(s) configured
                      </p>
                    </div>
                    {alerts.has_alerts && (
                      <Button
                        onClick={() => sendManualNotification(false)}
                        disabled={sendingNotification || !notificationSettings?.is_enabled}
                        className="bg-blue-600 hover:bg-blue-700"
                        size="sm"
                      >
                        {sendingNotification ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Send Alert Now
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Empty Bags Tab */}
          {activeTab === 'empty-bags' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-slate-800">Empty Feed Bags</h2>
                <Button 
                  onClick={() => {
                    setEmptyBagSaleForm({ ...emptyBagSaleForm, date: selectedDate });
                    setEmptyBagSaleDialogOpen(true);
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="record-sale-btn"
                >
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Record Sale
                </Button>
              </div>

              {/* Inventory Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-slate-600 to-slate-700 rounded-xl p-4 text-white">
                  <p className="text-slate-200 text-sm">Opening Balance</p>
                  <p className="text-2xl font-bold">{emptyBagSummary?.inventory?.opening || 0}</p>
                  <p className="text-slate-200 text-sm">bags</p>
                </div>
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white">
                  <p className="text-emerald-100 text-sm">+ Added (Consumed)</p>
                  <p className="text-2xl font-bold">{emptyBagSummary?.inventory?.added || 0}</p>
                  <p className="text-emerald-100 text-sm">bags today</p>
                </div>
                <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white">
                  <p className="text-orange-100 text-sm">- Sold</p>
                  <p className="text-2xl font-bold">{emptyBagSummary?.inventory?.sold || 0}</p>
                  <p className="text-orange-100 text-sm">bags today</p>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white relative">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-blue-100 text-sm">Closing Balance</p>
                      <p className="text-2xl font-bold">{emptyBagSummary?.inventory?.closing || 0}</p>
                      <p className="text-blue-100 text-sm">bags</p>
                    </div>
                    {isAdmin && (
                      <button
                        onClick={() => {
                          setEmptyBagAdjustForm({
                            actual_count: emptyBagSummary?.inventory?.closing || '',
                            reason: ''
                          });
                          setEmptyBagAdjustDialogOpen(true);
                        }}
                        className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                        title="Adjust closing balance"
                        data-testid="adjust-closing-btn"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {emptyBagSummary?.inventory?.manual_adjustment !== 0 && emptyBagSummary?.inventory?.manual_adjustment !== undefined && (
                    <div className="mt-2 text-xs bg-white/20 rounded px-2 py-1">
                      Adjusted: {emptyBagSummary.inventory.manual_adjustment > 0 ? '+' : ''}{emptyBagSummary.inventory.manual_adjustment}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Manual Adjustment Info */}
              {emptyBagSummary?.inventory?.manual_adjustment !== 0 && emptyBagSummary?.inventory?.manual_adjustment !== undefined && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <Pencil className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-amber-800">Manual Adjustment Applied</p>
                        <p className="text-sm text-amber-600">
                          {emptyBagSummary.inventory.manual_adjustment > 0 ? '+' : ''}{emptyBagSummary.inventory.manual_adjustment} bags adjusted
                        </p>
                      </div>
                    </div>
                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDeleteEmptyBagAdjustment}
                        className="text-amber-700 border-amber-300 hover:bg-amber-100"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Dozens and Singles Breakdown */}
              <div className="bg-white rounded-xl border p-4">
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Package className="w-5 h-5 text-amber-600" />
                  Inventory Breakdown (12 bags = 1 dozen)
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-amber-50 rounded-lg text-center">
                    <p className="text-amber-600 text-sm">Available Dozens</p>
                    <p className="text-3xl font-bold text-amber-700">{emptyBagSummary?.inventory?.dozens || 0}</p>
                    <p className="text-xs text-amber-500">({(emptyBagSummary?.inventory?.dozens || 0) * BAGS_PER_DOZEN} bags)</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <p className="text-slate-600 text-sm">Singles (Remainder)</p>
                    <p className="text-3xl font-bold text-slate-700">{emptyBagSummary?.inventory?.singles || 0}</p>
                    <p className="text-xs text-slate-500">bags</p>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-lg text-center md:col-span-1 col-span-2">
                    <p className="text-emerald-600 text-sm">Total Available</p>
                    <p className="text-3xl font-bold text-emerald-700">{emptyBagSummary?.inventory?.closing || 0}</p>
                    <p className="text-xs text-emerald-500">
                      ({emptyBagSummary?.inventory?.dozens || 0} doz + {emptyBagSummary?.inventory?.singles || 0} singles)
                    </p>
                  </div>
                </div>
              </div>

              {/* Consumption by Pen */}
              <div className="bg-white rounded-xl border p-4">
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Recycle className="w-5 h-5 text-green-600" />
                  Empty Bags from Pens Today
                </h3>
                <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                  {PENS.map(pen => {
                    const consumed = emptyBagSummary?.pen_consumption?.[pen] || 0;
                    return (
                      <div key={pen} className={`p-3 rounded-lg text-center ${consumed > 0 ? 'bg-green-50 border border-green-200' : 'bg-slate-50'}`}>
                        <p className="text-xs text-slate-500">{pen === 7 ? 'Brood' : pen === 8 ? 'Grower' : `Pen ${pen}`}</p>
                        <p className={`text-xl font-bold ${consumed > 0 ? 'text-green-700' : 'text-slate-400'}`}>
                          {consumed}
                        </p>
                        <p className="text-xs text-slate-400">bags</p>
                      </div>
                    );
                  })}
                </div>
                <p className="text-sm text-slate-500 mt-3 text-center">
                  Total: <span className="font-bold text-emerald-600">{emptyBagSummary?.inventory?.added || 0} bags</span> added to empty bag inventory today
                </p>
              </div>

              {/* Sales History */}
              <div className="bg-white rounded-xl border p-4">
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  Sales Today
                </h3>
                {(!emptyBagSummary?.sales || emptyBagSummary.sales.length === 0) ? (
                  <p className="text-slate-500 text-sm italic text-center py-4">No sales recorded for this date</p>
                ) : (
                  <div className="space-y-2">
                    {emptyBagSummary.sales.map((sale) => (
                      <div 
                        key={sale.id} 
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                        data-testid={`sale-${sale.id}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                              {sale.dozens_sold} dozen{sale.dozens_sold > 1 ? 's' : ''}
                            </span>
                            <span className="text-slate-600">
                              ({sale.bags_sold} bags)
                            </span>
                            {sale.price_per_dozen > 0 && (
                              <span className="text-green-600 font-medium">
                                ₦{sale.total_amount?.toLocaleString()}
                              </span>
                            )}
                            {sale.source === 'customer_account' && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-semibold uppercase tracking-wide">
                                From Account Records
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            {sale.buyer_name && <span>Buyer: {sale.buyer_name} • </span>}
                            Recorded by: {sale.recorded_by_name}
                            {sale.notes && <span className="ml-2">• {sale.notes}</span>}
                          </div>
                        </div>
                        {isAdmin && sale.source !== 'customer_account' && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditEmptyBagSale(sale)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteEmptyBagSale(sale.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Info Note */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-800">Daily Carryover</p>
                    <p className="text-sm text-blue-700">
                      Empty bags are automatically tallied when feed is consumed in pens. 
                      The closing balance carries over to the next day's opening balance.
                      Empty bags can be sold in dozens (12 bags = 1 dozen).
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Delivery Dialog */}
      <Dialog open={deliveryDialogOpen} onOpenChange={setDeliveryDialogOpen}>
        <DialogContent data-testid="delivery-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-emerald-600" />
              {editingDelivery ? 'Edit Feed Delivery' : 'Record Feed Delivery'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleDeliverySubmit} className="space-y-4">
            {/* Delivery Date */}
            <div>
              <Label htmlFor="delivery_date">Delivery Date</Label>
              <Input
                type="date"
                value={deliveryForm.delivery_date || selectedDate}
                onChange={(e) => setDeliveryForm({...deliveryForm, delivery_date: e.target.value})}
                data-testid="delivery-date"
              />
              <p className="text-xs text-slate-500 mt-1">Leave blank to use the selected date ({selectedDate})</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="feed_type">Feed Type *</Label>
                <Select
                  value={deliveryForm.feed_type}
                  onValueChange={(v) => setDeliveryForm({...deliveryForm, feed_type: v})}
                >
                  <SelectTrigger data-testid="delivery-feed-type">
                    <SelectValue placeholder="Select feed type" />
                  </SelectTrigger>
                  <SelectContent>
                    {FEED_TYPES.map(ft => (
                      <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="vendor">Vendor *</Label>
                <Select
                  value={deliveryForm.vendor}
                  onValueChange={(v) => setDeliveryForm({...deliveryForm, vendor: v})}
                >
                  <SelectTrigger data-testid="delivery-vendor">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {VENDORS.map(v => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bags_delivered">Bags Delivered *</Label>
                <Input
                  type="number"
                  min="1"
                  value={deliveryForm.bags_delivered}
                  onChange={(e) => setDeliveryForm({...deliveryForm, bags_delivered: e.target.value})}
                  placeholder="Number of bags"
                  data-testid="delivery-bags"
                />
              </div>
              <div>
                <Label htmlFor="damaged_bags">Damaged Bags</Label>
                <Input
                  type="number"
                  min="0"
                  value={deliveryForm.damaged_bags}
                  onChange={(e) => setDeliveryForm({...deliveryForm, damaged_bags: e.target.value})}
                  placeholder="0"
                  data-testid="delivery-damaged"
                />
              </div>
            </div>
            
            {parseInt(deliveryForm.damaged_bags) > 0 && (
              <div className="flex items-center space-x-2 p-3 bg-amber-50 rounded-lg">
                <Checkbox
                  id="returned"
                  checked={deliveryForm.returned_to_vendor}
                  onCheckedChange={(checked) => setDeliveryForm({...deliveryForm, returned_to_vendor: checked})}
                  data-testid="delivery-returned"
                />
                <label htmlFor="returned" className="text-sm text-amber-800 cursor-pointer">
                  Damaged bags were returned to vendor
                </label>
              </div>
            )}
            
            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                value={deliveryForm.notes}
                onChange={(e) => setDeliveryForm({...deliveryForm, notes: e.target.value})}
                placeholder="Any additional notes..."
                rows={2}
                data-testid="delivery-notes"
              />
            </div>
            
            {deliveryForm.bags_delivered && (
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p className="text-slate-600">
                  Net bags to inventory: <span className="font-bold text-emerald-600">
                    {parseInt(deliveryForm.bags_delivered || 0) - (deliveryForm.returned_to_vendor ? parseInt(deliveryForm.damaged_bags || 0) : 0)}
                  </span>
                </p>
              </div>
            )}
            
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="submit-delivery">
                {editingDelivery ? 'Update Delivery' : 'Record Delivery'}
              </Button>
              <Button type="button" variant="outline" onClick={() => {
                setDeliveryDialogOpen(false);
                setEditingDelivery(null);
                setDeliveryForm({
                  delivery_date: '',
                  feed_type: '',
                  vendor: '',
                  bags_delivered: '',
                  damaged_bags: '0',
                  returned_to_vendor: false,
                  notes: ''
                });
              }}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Usage Dialog */}
      <Dialog open={usageDialogOpen} onOpenChange={setUsageDialogOpen}>
        <DialogContent data-testid="usage-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-600" />
              {editingUsage ? 'Edit Pen Feed Usage' : 'Record Pen Feed Usage'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUsageSubmit} className="space-y-4">
            {/* Usage Date - for editing */}
            {editingUsage && (
              <div>
                <Label htmlFor="usage_date">Date</Label>
                <Input
                  type="date"
                  value={usageForm.usage_date || selectedDate}
                  onChange={(e) => setUsageForm({...usageForm, usage_date: e.target.value})}
                  data-testid="usage-date"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pen">Pen Number *</Label>
                <Select
                  value={usageForm.pen_number}
                  onValueChange={(v) => setUsageForm({...usageForm, pen_number: v})}
                >
                  <SelectTrigger data-testid="usage-pen">
                    <SelectValue placeholder="Select pen" />
                  </SelectTrigger>
                  <SelectContent>
                    {PENS.map(p => (
                      <SelectItem key={p} value={String(p)}>{getPenLabel(p)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="feed_type">Feed Type *</Label>
                <Select
                  value={usageForm.feed_type}
                  onValueChange={(v) => setUsageForm({...usageForm, feed_type: v})}
                >
                  <SelectTrigger data-testid="usage-feed-type">
                    <SelectValue placeholder="Select feed type" />
                  </SelectTrigger>
                  <SelectContent>
                    {FEED_TYPES.map(ft => (
                      <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bags_taken">Bags Taken to Pen *</Label>
                <Input
                  type="number"
                  min="1"
                  value={usageForm.bags_taken}
                  onChange={(e) => setUsageForm({...usageForm, bags_taken: e.target.value})}
                  placeholder="Bags taken"
                  data-testid="usage-taken"
                />
              </div>
              <div>
                <Label htmlFor="bags_consumed">Bags Fully Consumed *</Label>
                <Input
                  type="number"
                  min="0"
                  value={usageForm.bags_consumed}
                  onChange={(e) => setUsageForm({...usageForm, bags_consumed: e.target.value})}
                  placeholder="Bags consumed"
                  data-testid="usage-consumed"
                />
              </div>
            </div>
            
            {usageForm.bags_taken && usageForm.bags_consumed && (
              <div className="p-3 bg-slate-50 rounded-lg text-sm space-y-1">
                <p className="text-slate-600">
                  Bags returned to inventory: <span className="font-bold text-blue-600">
                    {Math.max(0, parseInt(usageForm.bags_taken || 0) - parseInt(usageForm.bags_consumed || 0))}
                  </span>
                </p>
                <p className="text-slate-600">
                  Total kg consumed: <span className="font-bold text-purple-600">
                    {parseInt(usageForm.bags_consumed || 0) * KG_PER_BAG} kg
                  </span>
                </p>
              </div>
            )}
            
            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                value={usageForm.notes}
                onChange={(e) => setUsageForm({...usageForm, notes: e.target.value})}
                placeholder="Any additional notes..."
                rows={2}
                data-testid="usage-notes"
              />
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="submit-usage">
                {editingUsage ? 'Update Usage' : 'Record Usage'}
              </Button>
              <Button type="button" variant="outline" onClick={() => {
                setUsageDialogOpen(false);
                setEditingUsage(null);
                setUsageForm({
                  usage_date: '',
                  pen_number: '',
                  feed_type: '',
                  bags_taken: '',
                  bags_consumed: '',
                  notes: ''
                });
              }}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Alert Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="alert-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-emerald-600" />
              Alert Threshold Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Configure when alerts should be triggered for each feed type. 
              Alerts show when closing inventory falls below these thresholds.
            </p>
            
            <div className="space-y-4">
              {FEED_TYPES.map(feedType => {
                const settings = alertSettings.find(s => s.feed_type === feedType.value) || {
                  low_stock_threshold: 10,
                  critical_stock_threshold: 5,
                  is_enabled: true
                };
                
                return (
                  <div key={feedType.value} className="p-4 bg-slate-50 rounded-lg" data-testid={`settings-${feedType.value}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Wheat className="w-4 h-4 text-amber-600" />
                        <span className="font-medium text-slate-800">{feedType.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">Alerts</span>
                        <Switch
                          checked={settings.is_enabled}
                          onCheckedChange={(checked) => updateAlertSetting(feedType.value, 'is_enabled', checked)}
                          data-testid={`toggle-${feedType.value}`}
                        />
                      </div>
                    </div>
                    
                    {settings.is_enabled && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-amber-600">Low Stock Threshold</Label>
                          <Input
                            type="number"
                            min="0"
                            value={settings.low_stock_threshold}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 0;
                              updateAlertSetting(feedType.value, 'low_stock_threshold', val);
                            }}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-red-600">Critical Threshold</Label>
                          <Input
                            type="number"
                            min="0"
                            value={settings.critical_stock_threshold}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 0;
                              updateAlertSetting(feedType.value, 'critical_stock_threshold', val);
                            }}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    )}
                    
                    {settings.updated_by_name && (
                      <p className="text-xs text-slate-400 mt-2">
                        Last updated by {settings.updated_by_name}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Notification Settings Dialog */}
      <Dialog open={notificationDialogOpen} onOpenChange={setNotificationDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="notification-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Email Notification Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-800">Enable Email Notifications</p>
                <p className="text-sm text-slate-500">Send alerts when feed stock is low</p>
              </div>
              <Switch
                checked={notificationSettings?.is_enabled || false}
                onCheckedChange={(checked) => updateNotificationSetting('is_enabled', checked)}
                data-testid="notification-toggle"
              />
            </div>

            {/* Recipient Emails */}
            <div className="space-y-2">
              <Label>Recipient Email Addresses</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={newEmailInput}
                  onChange={(e) => setNewEmailInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addNotificationEmail())}
                  data-testid="add-email-input"
                />
                <Button onClick={addNotificationEmail} size="sm" className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-2 mt-2">
                {(notificationSettings?.notification_emails || []).map((email, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-100 rounded">
                    <span className="text-sm text-slate-700">{email}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeNotificationEmail(email)}
                      className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {(!notificationSettings?.notification_emails || notificationSettings.notification_emails.length === 0) && (
                  <p className="text-sm text-slate-500 italic">No recipients configured</p>
                )}
              </div>
            </div>

            {/* Alert Level Preferences */}
            <div className="space-y-3">
              <Label>Alert Level Preferences</Label>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-amber-50 rounded">
                  <span className="text-sm text-amber-800">Notify on Low Stock</span>
                  <Switch
                    checked={notificationSettings?.notify_on_low_stock ?? true}
                    onCheckedChange={(checked) => updateNotificationSetting('notify_on_low_stock', checked)}
                  />
                </div>
                <div className="flex items-center justify-between p-2 bg-orange-50 rounded">
                  <span className="text-sm text-orange-800">Notify on Critical Stock</span>
                  <Switch
                    checked={notificationSettings?.notify_on_critical ?? true}
                    onCheckedChange={(checked) => updateNotificationSetting('notify_on_critical', checked)}
                  />
                </div>
                <div className="flex items-center justify-between p-2 bg-red-50 rounded">
                  <span className="text-sm text-red-800">Notify on Out of Stock</span>
                  <Switch
                    checked={notificationSettings?.notify_on_out_of_stock ?? true}
                    onCheckedChange={(checked) => updateNotificationSetting('notify_on_out_of_stock', checked)}
                  />
                </div>
              </div>
            </div>

            {/* Cooldown Period */}
            <div className="space-y-2">
              <Label>Cooldown Period (Hours)</Label>
              <p className="text-xs text-slate-500">Prevent duplicate alerts within this time frame</p>
              <Input
                type="number"
                min="1"
                max="168"
                value={notificationSettings?.cooldown_hours || 24}
                onChange={(e) => updateNotificationSetting('cooldown_hours', parseInt(e.target.value) || 24)}
                className="w-24"
              />
            </div>

            {/* Recent Notification Logs */}
            {notificationLogs.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Recent Notifications
                </Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {notificationLogs.slice(0, 5).map((log, idx) => (
                    <div key={idx} className="text-xs p-2 bg-slate-50 rounded flex justify-between items-center">
                      <span className="font-medium">{log.feed_type?.replace('_', ' ')}</span>
                      <span className={`px-2 py-0.5 rounded ${
                        log.alert_level === 'out_of_stock' ? 'bg-red-100 text-red-700' :
                        log.alert_level === 'critical' ? 'bg-orange-100 text-orange-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {log.alert_level?.replace('_', ' ')}
                      </span>
                      <span className="text-slate-400">
                        {new Date(log.sent_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Send Test Button */}
            <div className="pt-2 border-t">
              <Button
                onClick={() => sendManualNotification(true)}
                disabled={sendingNotification || !notificationSettings?.notification_emails?.length}
                variant="outline"
                className="w-full"
              >
                {sendingNotification ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Test Notification
              </Button>
            </div>

            {notificationSettings?.updated_by_name && (
              <p className="text-xs text-slate-400 text-center">
                Last updated by {notificationSettings.updated_by_name}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Empty Bag Sale Dialog */}
      <Dialog open={emptyBagSaleDialogOpen} onOpenChange={setEmptyBagSaleDialogOpen}>
        <DialogContent data-testid="empty-bag-sale-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-amber-600" />
              {editingEmptyBagSale ? 'Edit Empty Bag Sale' : 'Record Empty Bag Sale'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEmptyBagSaleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="sale_date">Sale Date</Label>
              <Input
                type="date"
                value={emptyBagSaleForm.date || selectedDate}
                onChange={(e) => setEmptyBagSaleForm({...emptyBagSaleForm, date: e.target.value})}
                data-testid="sale-date"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dozens_sold">Dozens Sold *</Label>
                <Input
                  type="number"
                  min="1"
                  value={emptyBagSaleForm.dozens_sold}
                  onChange={(e) => setEmptyBagSaleForm({...emptyBagSaleForm, dozens_sold: e.target.value})}
                  placeholder="Number of dozens"
                  data-testid="sale-dozens"
                />
                {emptyBagSaleForm.dozens_sold && (
                  <p className="text-xs text-slate-500 mt-1">
                    = {parseInt(emptyBagSaleForm.dozens_sold) * BAGS_PER_DOZEN} bags
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="price_per_dozen">Price per Dozen (₦)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={emptyBagSaleForm.price_per_dozen}
                  onChange={(e) => setEmptyBagSaleForm({...emptyBagSaleForm, price_per_dozen: e.target.value})}
                  placeholder="0.00"
                  data-testid="sale-price"
                />
              </div>
            </div>

            {emptyBagSaleForm.dozens_sold && emptyBagSaleForm.price_per_dozen && (
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-800">
                  Total Amount: <span className="font-bold">₦{(parseInt(emptyBagSaleForm.dozens_sold || 0) * parseFloat(emptyBagSaleForm.price_per_dozen || 0)).toLocaleString()}</span>
                </p>
              </div>
            )}
            
            <div>
              <Label htmlFor="buyer_name">Buyer Name (Optional)</Label>
              <Input
                value={emptyBagSaleForm.buyer_name}
                onChange={(e) => setEmptyBagSaleForm({...emptyBagSaleForm, buyer_name: e.target.value})}
                placeholder="Name of buyer"
                data-testid="sale-buyer"
              />
            </div>
            
            <div>
              <Label htmlFor="sale_notes">Notes (Optional)</Label>
              <Textarea
                value={emptyBagSaleForm.notes}
                onChange={(e) => setEmptyBagSaleForm({...emptyBagSaleForm, notes: e.target.value})}
                placeholder="Any additional notes..."
                rows={2}
                data-testid="sale-notes"
              />
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="submit-sale">
                {editingEmptyBagSale ? 'Update Sale' : 'Record Sale'}
              </Button>
              <Button type="button" variant="outline" onClick={() => {
                setEmptyBagSaleDialogOpen(false);
                setEditingEmptyBagSale(null);
                setEmptyBagSaleForm({
                  date: '',
                  dozens_sold: '',
                  price_per_dozen: '',
                  buyer_name: '',
                  notes: ''
                });
              }}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Empty Bag Adjustment Dialog */}
      <Dialog open={emptyBagAdjustDialogOpen} onOpenChange={setEmptyBagAdjustDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-600" />
              Adjust Closing Balance
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEmptyBagAdjustSubmit} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700">
                <strong>Date:</strong> {selectedDate}
              </p>
              <p className="text-sm text-blue-700 mt-1">
                <strong>Current Calculated:</strong> {emptyBagSummary?.inventory?.closing || 0} bags
              </p>
            </div>
            
            <div>
              <Label htmlFor="actual_count">Actual Physical Count *</Label>
              <Input
                id="actual_count"
                type="number"
                min="0"
                value={emptyBagAdjustForm.actual_count}
                onChange={(e) => setEmptyBagAdjustForm({...emptyBagAdjustForm, actual_count: e.target.value})}
                placeholder="Enter actual bag count on ground"
                className="mt-1"
                required
                data-testid="actual-count-input"
              />
              {emptyBagAdjustForm.actual_count && emptyBagSummary?.inventory?.closing && (
                <p className="text-sm mt-1 text-slate-500">
                  Adjustment: {parseInt(emptyBagAdjustForm.actual_count) - emptyBagSummary.inventory.closing >= 0 ? '+' : ''}
                  {parseInt(emptyBagAdjustForm.actual_count) - emptyBagSummary.inventory.closing} bags
                </p>
              )}
            </div>
            
            <div>
              <Label htmlFor="reason">Reason for Adjustment *</Label>
              <Textarea
                id="reason"
                value={emptyBagAdjustForm.reason}
                onChange={(e) => setEmptyBagAdjustForm({...emptyBagAdjustForm, reason: e.target.value})}
                placeholder="e.g., Physical count mismatch, bags damaged, bags misplaced..."
                className="mt-1"
                required
                data-testid="adjustment-reason"
              />
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="submit-adjustment">
                Apply Adjustment
              </Button>
              <Button type="button" variant="outline" onClick={() => {
                setEmptyBagAdjustDialogOpen(false);
                setEmptyBagAdjustForm({ actual_count: '', reason: '' });
              }}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Feed Closing Adjustment Dialog (Admin) */}
      <Dialog open={feedAdjustDialogOpen} onOpenChange={setFeedAdjustDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-emerald-600" />
              Edit Closing Balance
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleFeedAdjustSubmit} className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-sm text-emerald-700">
                <strong>Feed Type:</strong> {feedAdjustForm.feed_label}
              </p>
              <p className="text-sm text-emerald-700 mt-1">
                <strong>Date:</strong> {selectedDate}
              </p>
              <p className="text-sm text-emerald-700 mt-1">
                <strong>System Calculated Closing:</strong> {feedAdjustForm.calculated_closing} bags
              </p>
            </div>

            <div>
              <Label htmlFor="feed_actual_count">Actual Physical Count *</Label>
              <Input
                id="feed_actual_count"
                type="number"
                min="0"
                value={feedAdjustForm.actual_count}
                onChange={(e) => setFeedAdjustForm({...feedAdjustForm, actual_count: e.target.value})}
                placeholder="Enter actual bag count on ground"
                className="mt-1"
                required
                data-testid="feed-actual-count-input"
              />
              {feedAdjustForm.actual_count !== '' && (
                <p className="text-sm mt-1 text-slate-500">
                  Adjustment: {parseInt(feedAdjustForm.actual_count) - feedAdjustForm.calculated_closing >= 0 ? '+' : ''}
                  {parseInt(feedAdjustForm.actual_count) - feedAdjustForm.calculated_closing} bags
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="feed_reason">Reason for Adjustment *</Label>
              <div className="flex flex-wrap gap-1.5 mt-1 mb-2">
                {REASON_PRESETS.map(preset => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setFeedAdjustForm({...feedAdjustForm, reason: preset})}
                    className={`text-xs px-2 py-1 rounded-full border transition-colors ${feedAdjustForm.reason === preset ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                    data-testid={`feed-reason-preset-${preset}`}
                  >
                    {preset}
                  </button>
                ))}
              </div>
              <Textarea
                id="feed_reason"
                value={feedAdjustForm.reason}
                onChange={(e) => setFeedAdjustForm({...feedAdjustForm, reason: e.target.value})}
                placeholder="e.g., Physical count mismatch, spillage, bags moved between stores..."
                className="mt-1"
                required
                data-testid="feed-adjustment-reason"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
              <p className="text-xs text-blue-700">
                This new closing balance carries over as the next day's opening balance for {feedAdjustForm.feed_label}.
              </p>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="submit-feed-adjustment">
                Apply Adjustment
              </Button>
              <Button type="button" variant="outline" onClick={() => setFeedAdjustDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stock Take (Bulk Reconcile) Dialog */}
      <Dialog open={stockTakeDialogOpen} onOpenChange={setStockTakeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-emerald-600" />
              Stock Take — Reconcile All Feeds
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleStockTakeSubmit} className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-sm text-emerald-700"><strong>Date:</strong> {selectedDate}</p>
              <p className="text-xs text-emerald-600 mt-1">Enter the actual physical count for each feed type. Blank rows are skipped.</p>
            </div>
            <div className="space-y-2">
              {FEED_TYPES.map(ft => {
                const calc = dailySummary?.summary?.[ft.value]?.inventory?.closing ?? 0;
                const val = stockTakeForm.counts[ft.value];
                const diff = val !== '' && val !== undefined ? parseInt(val) - calc : null;
                return (
                  <div key={ft.value} className="flex items-center gap-3">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-700">{ft.label}</p>
                      <p className="text-xs text-slate-400">Current: {calc}</p>
                    </div>
                    <Input
                      type="number"
                      min="0"
                      value={val ?? ''}
                      onChange={(e) => setStockTakeForm({...stockTakeForm, counts: {...stockTakeForm.counts, [ft.value]: e.target.value}})}
                      className="w-28"
                      placeholder="Actual"
                      data-testid={`stock-take-${ft.value}`}
                    />
                    <div className="w-16 text-right text-xs">
                      {diff !== null && !isNaN(diff) && (
                        <span className={Math.abs(diff) >= VARIANCE_THRESHOLD ? 'text-red-600 font-semibold' : 'text-slate-500'}>
                          {diff >= 0 ? '+' : ''}{diff}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div>
              <Label htmlFor="stock_take_reason">Reason *</Label>
              <div className="flex flex-wrap gap-1.5 mt-1 mb-2">
                {REASON_PRESETS.map(preset => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setStockTakeForm({...stockTakeForm, reason: preset})}
                    className={`text-xs px-2 py-1 rounded-full border transition-colors ${stockTakeForm.reason === preset ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                    data-testid={`stock-take-reason-preset-${preset}`}
                  >
                    {preset}
                  </button>
                ))}
              </div>
              <Textarea
                id="stock_take_reason"
                value={stockTakeForm.reason}
                onChange={(e) => setStockTakeForm({...stockTakeForm, reason: e.target.value})}
                placeholder="e.g., Monthly physical stock-take"
                required
                data-testid="stock-take-reason"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="submit-stock-take">
                Apply Stock Take
              </Button>
              <Button type="button" variant="outline" onClick={() => setStockTakeDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reconciliation Log Dialog */}
      <Dialog open={feedLogDialogOpen} onOpenChange={setFeedLogDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-slate-600" />
              Feed Reconciliation Log
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap gap-1.5 mb-2">
            <button
              type="button"
              onClick={() => openFeedLogDialog('all')}
              className={`text-xs px-2 py-1 rounded-full border ${feedLogFilter === 'all' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
              data-testid="log-filter-all"
            >
              All Feeds
            </button>
            {FEED_TYPES.map(ft => (
              <button
                key={ft.value}
                type="button"
                onClick={() => openFeedLogDialog(ft.value)}
                className={`text-xs px-2 py-1 rounded-full border ${feedLogFilter === ft.value ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                data-testid={`log-filter-${ft.value}`}
              >
                {ft.label}
              </button>
            ))}
          </div>
          <div className="max-h-[55vh] overflow-y-auto" data-testid="feed-log-list">
            {feedLogLoading ? (
              <p className="text-center text-slate-500 py-8">Loading...</p>
            ) : feedLogs.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No reconciliation history yet.</p>
            ) : (
              <div className="space-y-2">
                {feedLogs.map((log, idx) => {
                  const ftLabel = FEED_TYPES.find(f => f.value === log.feed_type)?.label || log.feed_type;
                  const isRemove = log.action === 'remove';
                  const highVar = Math.abs(log.adjustment_amount || 0) >= VARIANCE_THRESHOLD;
                  return (
                    <div key={log.id || idx} className="border rounded-lg p-3 bg-white" data-testid="feed-log-entry">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-800 text-sm">{ftLabel}</span>
                          <span className="text-xs text-slate-400">{log.date}</span>
                          {isRemove ? (
                            <Badge className="bg-slate-500 hover:bg-slate-500 text-white text-[10px] px-1.5 py-0">Removed</Badge>
                          ) : (
                            <Badge className={`text-[10px] px-1.5 py-0 text-white ${highVar ? 'bg-red-600 hover:bg-red-600' : 'bg-emerald-600 hover:bg-emerald-600'}`}>
                              {log.adjustment_amount > 0 ? '+' : ''}{log.adjustment_amount} bags
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-slate-400 whitespace-nowrap">
                          {log.adjusted_at ? new Date(log.adjusted_at).toLocaleString() : ''}
                        </span>
                      </div>
                      {!isRemove && (
                        <p className="text-xs text-slate-500 mt-1">
                          System: {log.calculated_count} → Actual: {log.actual_count}
                        </p>
                      )}
                      <p className="text-sm text-slate-600 mt-1">{log.reason}</p>
                      <p className="text-xs text-slate-400 mt-1">by {log.adjusted_by}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Separate component to list all usage records
const UsageRecordsList = ({ dailySummary, selectedDate, onDelete, onEdit, canDelete, canEdit }) => {
  const [usageRecords, setUsageRecords] = useState([]);
  
  useEffect(() => {
    fetchUsageRecords();
  }, [selectedDate]);

  const fetchUsageRecords = async () => {
    try {
      const response = await axios.get(`${API}/chicken-feed/pen-usage?date=${selectedDate}`, getAuthHeader());
      setUsageRecords(response.data);
    } catch (error) {
      console.error('Error fetching usage records:', error);
    }
  };

  if (usageRecords.length === 0) {
    return <p className="text-slate-500 text-sm italic">No usage records for this date</p>;
  }

  return (
    <div className="space-y-2">
      {usageRecords.map((record) => (
        <div 
          key={record.id} 
          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
          data-testid={`usage-record-${record.id}`}
        >
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                {getPenLabel(record.pen_number)}
              </span>
              <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                {getFeedTypeLabel(record.feed_type)}
              </span>
              <span className="font-medium">{record.bags_consumed} bags consumed ({record.kg_consumed} kg)</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Taken: {record.bags_taken} • Returned: {record.bags_returned} • By: {record.recorded_by_name}
            </div>
          </div>
          {(canEdit || canDelete) && (
            <div className="flex gap-1">
              {canEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(record)}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  data-testid={`edit-usage-${record.id}`}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    onDelete(record.id);
                    fetchUsageRecords();
                  }}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid={`delete-usage-${record.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ChickenFeedAudit;
